import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../../components/Form";
import { WarehouseMap } from "../../../../../../models/warehouse.map";
import { MapCell } from "../../../../../../models/warehouse.mapcell";
import { MapStatus } from "../../../../../../util/enums";
import { PreviewStatus } from "./PreviewStatus";
import {
  MapRow,
  MapSquare,
  Square,
  MapInput,
  TopViewText,
  GridContainerTitle,
  ContainerMap,
  MapCellsContainer,
  CellButton,
  MapCellsWrapper,
  ContainerMapWrapper,
  MapCellsLevelsWrapper,
} from "./styles";

enum AxisNames {
  ROW_NAME = "rowName",
  COLUMN_NAME = "columnName",
  LEVEL_NAME = "levelName",
}

enum IndexNames {
  INDEX_ROW = "indexRow",
  INDEX_COLUMN = "indexColumn",
  INDEX_LEVEL = "indexLevel",
}

interface Props {
  map: WarehouseMap;
  mapCellSelected?: MapCell;
  setMapCellSelected?: (mapCell?: MapCell) => void;
  viewMode?: boolean;
}

export const MapComponent = ({
  map,
  mapCellSelected,
  setMapCellSelected,
  viewMode = false,
}: Props) => {
  const { t: translate } = useTranslation();
  const [indexColumn, setIndexColumn] = useState<number>();
  const [indexRow, setIndexRow] = useState<number>();
  const [indexLevel, setIndexLevel] = useState<number>();
  const mapScrollRef = useRef<HTMLDivElement>(null);

  const { configColumn, configLevel, configRow, mapCells } = map;

  if (!configColumn || !configLevel || !configRow || !mapCells?.length) return <></>;

  const { size: columnsLength } = configColumn;
  const { size: levelsLength } = configLevel;
  const { size: rowsLength } = configRow;

  useEffect(() => {
    setIndexLevel(undefined);
    setIndexRow(undefined);
    setIndexColumn(undefined);
  }, [map]);

  const groupBy = useCallback(
    (axisName: AxisNames, indexName: IndexNames, mapCellsSelected: MapCell[]) => {
      const names: string[] = [];

      if (mapCellsSelected?.length) {
        for (const mapCell of mapCellsSelected) {
          if (!names?.includes(mapCell[axisName])) {
            names[mapCell[indexName]] = mapCell[axisName];
          }
        }
      }

      return names;
    },
    [map]
  );

  const renderInput = (name: string) => {
    return <MapInput value={name} type={"text"} isViewMode={true} disabled={true} />;
  };

  const getSquareColor = (cellStatuses: string[]) => {
    if (cellStatuses.every((cellStatus) => cellStatus === MapStatus.EMPTY)) {
      return MapStatus.EMPTY;
    }

    if (cellStatuses.every((cellStatus) => cellStatus === MapStatus.INACTIVE)) {
      return MapStatus.INACTIVE;
    }

    if (cellStatuses.every((cellStatus) => cellStatus === MapStatus.LOADED)) {
      return MapStatus.LOADED;
    }

    return MapStatus.MIXED;
  };

  const renderSquareToRowAndColumn = (indexRowButton: number, indexColumnButton: number) => {
    const mapCellsStatuses: string[] = [];

    for (let level = 0; level < levelsLength; level += 1) {
      const mapCellToStatus = mapCells?.find(
        (mapCell) =>
          mapCell?.indexLevel === level &&
          mapCell?.indexRow === indexRowButton &&
          mapCell?.indexColumn === indexColumnButton
      );

      if (!mapCellToStatus?.isActive) {
        mapCellsStatuses.push(MapStatus.INACTIVE);
      } else {
        const mapCellStatus =
          (mapCellToStatus?.storageBoxes && mapCellToStatus.storageBoxes.length > 0) ||
          (mapCellToStatus?.itemsJobs && mapCellToStatus?.itemsJobs?.length > 0)
            ? MapStatus.LOADED
            : MapStatus.EMPTY;

        mapCellsStatuses.push(mapCellStatus);
      }
    }

    const mapCell = mapCells?.filter(
      (mapCell) =>
        mapCell?.indexLevel === 0 &&
        mapCell?.indexRow === indexRowButton &&
        mapCell?.indexColumn === indexColumnButton
    )?.[0];

    const scrollToEnd = () => {
      setTimeout(() => {
        if (mapScrollRef.current) {
          mapScrollRef.current.scrollTo({
            left: mapScrollRef.current.scrollWidth,
            behavior: "smooth",
          });
        }
      }, 0);
    };

    return (
      <MapSquare
        selected={indexRowButton === indexRow && indexColumnButton === indexColumn}
        status={getSquareColor(mapCellsStatuses)}
      >
        <CellButton
          disabled={
            viewMode || mapCellsStatuses.every((cellStatus) => cellStatus === MapStatus.INACTIVE)
          }
          onClick={() => {
            setIndexColumn(indexColumnButton);
            setIndexRow(indexRowButton);
            scrollToEnd();
            if (setMapCellSelected && mapCell) setMapCellSelected(mapCell);
          }}
        />
      </MapSquare>
    );
  };

  const renderSquareToLevel = (indexLevelButton: number) => {
    const mapCell = mapCells?.filter(
      (mapCell) =>
        mapCell?.indexLevel === indexLevelButton &&
        mapCell?.indexRow === indexRow &&
        mapCell?.indexColumn === indexColumn
    )?.[0];

    const mapCellStatus =
      (mapCell?.storageBoxes && mapCell.storageBoxes.length > 0) ||
      (mapCell?.itemsJobs && mapCell?.itemsJobs?.length > 0)
        ? MapStatus.LOADED
        : MapStatus.EMPTY;

    return (
      <MapSquare
        selected={indexLevelButton === indexLevel}
        status={mapCell?.isActive ? mapCellStatus : MapStatus.INACTIVE}
      >
        <Button
          disabled={viewMode || !mapCell?.isActive}
          onClick={() => {
            setIndexLevel(indexLevelButton);
            if (setMapCellSelected && mapCell) setMapCellSelected(mapCell);
          }}
        />
      </MapSquare>
    );
  };

  const mountGrid = (rowsNames: string[], columnsNames: string[], children: React.ReactNode) => {
    if (rowsLength > 0 && columnsLength > 0 && levelsLength > 0) {
      const gridMap = [];

      for (let row = 0; row < rowsLength; row += 1) {
        const currentRow = [];

        for (let column = 0; column < columnsLength; column += 1) {
          if (column === 0) {
            currentRow.push(renderInput(rowsNames[row]));
          }
          currentRow.push(renderSquareToRowAndColumn(row, column));
        }

        gridMap.push(currentRow);
      }

      const currentRow = [];

      for (let column = 0; column < columnsLength; column += 1) {
        if (column === 0) {
          currentRow.push(<Square />);
        }
        currentRow.push(renderInput(columnsNames[column]));
      }

      gridMap.push(currentRow);

      return (
        <MapCellsWrapper item ref={mapScrollRef}>
          <TopViewText>{translate("topView")}</TopViewText>

          <MapCellsContainer>
            <Grid item>
              {gridMap.map((row, index) => {
                return (
                  <MapRow key={index}>
                    {row.map((mapElement: React.ReactNode) => {
                      return mapElement;
                    })}
                  </MapRow>
                );
              })}
            </Grid>
            {children}
          </MapCellsContainer>
        </MapCellsWrapper>
      );
    }
  };

  const mountLevelGrid = (indexColumn?: number, indexRow?: number, axisName?: string) => {
    if (indexColumn === undefined || indexRow === undefined) return <></>;

    const getLevels = () => {
      return mapCells?.filter(
        (mapCell) => mapCell.indexColumn === indexColumn && mapCell.indexRow === indexRow
      );
    };

    const levelsNames = groupBy(AxisNames.LEVEL_NAME, IndexNames.INDEX_LEVEL, getLevels());

    const gridLevel = [];

    for (let level = 0; level < levelsLength; level += 1) {
      gridLevel.push([renderSquareToLevel(level), renderInput(levelsNames[level])]);
    }

    return (
      <MapCellsLevelsWrapper item>
        <TopViewText>{axisName}</TopViewText>

        <Grid item>
          {gridLevel.reverse().map((level, index) => {
            return (
              <MapRow key={index}>
                {level.map((mapElement: React.ReactNode) => {
                  return mapElement;
                })}
              </MapRow>
            );
          })}
        </Grid>
      </MapCellsLevelsWrapper>
    );
  };

  const buildMapComponent = useMemo(() => {
    if (rowsLength > 0 && columnsLength > 0 && levelsLength > 0) {
      if (mapCellSelected) {
        setIndexColumn(mapCellSelected?.indexColumn);
        setIndexRow(mapCellSelected?.indexRow);
        setIndexLevel(mapCellSelected?.indexLevel);
      }
      const rowsNames = groupBy(AxisNames.ROW_NAME, IndexNames.INDEX_ROW, mapCells);
      const columnsNames = groupBy(AxisNames.COLUMN_NAME, IndexNames.INDEX_COLUMN, mapCells);

      const getLevelGridName = (
        indexColumn?: number,
        indexRow?: number,
        rowsNames?: string[],
        columnsNames?: string[]
      ) => {
        if (
          rowsNames?.length &&
          columnsNames?.length &&
          indexRow !== undefined &&
          indexColumn !== undefined
        ) {
          const rowName = rowsNames[indexRow];
          const columnName = columnsNames[indexColumn];

          return `${rowName}${columnName}`;
        }

        return "";
      };

      return (
        <ContainerMap>
          <ContainerMapWrapper container>
            <PreviewStatus displayPreviewTitle={false} />
            <Grid item xs={12}>
              <GridContainerTitle>{translate("selectNewLocation")}</GridContainerTitle>
            </Grid>
            <Grid item xs={12} container spacing={3}>
              {mountGrid(
                rowsNames,
                columnsNames,
                mountLevelGrid(
                  indexColumn,
                  indexRow,
                  getLevelGridName(indexColumn, indexRow, rowsNames, columnsNames)
                )
              )}
            </Grid>
          </ContainerMapWrapper>
        </ContainerMap>
      );
    }

    return <></>;
  }, [map, indexRow, indexColumn, indexLevel, mapCellSelected]);

  return buildMapComponent;
};
